import {BDModalComponent, BDModalService} from '@brother-apps/brother-dealerapp-shared-lib';
import {HttpErrorResponse} from '@angular/common/http';
import {BDCPFolder} from '../../../../models/folder.model';
import {BDCPShareService} from './../share.service';
import {IBDCPFolderService} from './folder.interface';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {MyHttpClient} from '../../../../../../../shared/services/my-http-client.service';
import {ToastService} from '../../../../../../../shared/services/toast.service';

@Injectable()
export class BDCPFolderService extends IBDCPFolderService {

    public folders: BDCPFolder[];

    private modal: BDModalComponent;
    private endpoint = '/users/{userId}/folders/';

    private skuToSave: string;

    public constructor(
        private router: Router,
        private httpClient: MyHttpClient,
        private share: BDCPShareService,
        private modalService: BDModalService,
         private toastService: ToastService
    ) {
        super();
    }

    public init(modal: BDModalComponent): void {
        this.modal = modal;
    }

    public async getAll(): Promise<BDCPFolder[]> {
        const response = await firstValueFrom(
            this.httpClient.get<{ folders: BDCPFolder[] }>(this.share.sharedModel?.apiUrl + this.getEndpoint(), {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                }
            })
        );
        return <BDCPFolder[]>response.folders;
    }

    public async get(folderId: number, includeProducts?: boolean): Promise<BDCPFolder> {
        return <BDCPFolder>await firstValueFrom(
            this.httpClient.get(this.share.sharedModel?.apiUrl + this.getEndpoint() + folderId, {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                },
                params: {
                    includeProducts: includeProducts.toString()
                }
            })
        );
    }

    public async create(name: string): Promise<BDCPFolder> {
        try {
            return await this._create(name);
        } catch (ex) {
            if (ex instanceof HttpErrorResponse) {
                const httpEx = <HttpErrorResponse>ex;
                if (httpEx.error.message && httpEx.error.message.indexOf('Folder with name:') > -1) {
                    this.modalService.open(
                        'Achtung',
                        `Es existiert bereits eine Merkliste mit dem Namen "${name}". Bitte wählen Sie einen anderen Namen`
                    );
                }
            }
            return null;
        }
    }

    private async _create(name: string): Promise<BDCPFolder> {
        return await firstValueFrom(
            this.httpClient.post<BDCPFolder>(this.share.sharedModel?.apiUrl + this.getEndpoint(), {
                name: name
            }, {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                }
            })
        );
    }

    public async update(folderId: number, name: string): Promise<void> {
        try {
            await firstValueFrom(
                this.httpClient.put<BDCPFolder>(this.share.sharedModel?.apiUrl + this.getEndpoint() + folderId, {
                    name: name
                }, {
                    headers: {
                        'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                    }
                })
            );
        } catch (ex) {
            if (ex instanceof HttpErrorResponse) {
                const httpEx = <HttpErrorResponse>ex;
                if (httpEx.error.message && httpEx.error.message.indexOf('Folder with name:') > -1) {
                    this.modalService.open(
                        'Achtung',
                        `Es existiert bereits eine Merkliste mit dem Namen "${name}". Bitte wählen Sie einen anderen Namen`
                    );
                }
            } else {
                return null;
            }
        }
    }

    public async duplicate(folderId: number): Promise<void> {
        const template = await this.get(folderId, true);
        const folder = await this._duplicate(template.name);
        for (const product of template.favoriteProducts) {
            this.skuToSave = product.manufacturerSKU;
            await this._addProduct(folder.id);
        }
        this.toastService.showSuccessWide('<b>Zur Merkliste</b><br> Merkliste erfolgreich dupliziert.');
        await this.router.navigate([
            '/products', 'bookmarks', 'details', folder.id
        ]);
    }

    private async _duplicate(name: string): Promise<BDCPFolder> {
        name += ' (Kopie)';
        let folder: BDCPFolder;
        try {
            folder = await this._create(name);
        } catch {
        }
        let i = 1;
        while (!folder) {
            if (i === 1) {
                name = name.replace(' (Kopie)', ` (Kopie ${i})`);
            } else {
                name = name.replace(` (Kopie ${(i - 1)})`, ` (Kopie ${i})`);
            }
            try {
                folder = await this._create(name);
            } catch {
            }
            i++;
        }
        return folder;
    }

    public async delete(folderId: number): Promise<void> {
        await firstValueFrom(this.httpClient.delete(this.share.sharedModel?.apiUrl + this.getEndpoint() + folderId, {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                }
            })
        );
    }

    public async addProduct(folderId: number): Promise<void> {
        try {
            await this._addProduct(folderId);
            this.toastService.showSuccessWide('<b>Zur Merkliste</b><br>Erfolgreich zur Merkliste hinzugefügt.');
            await this.router.navigate(['/products', 'bookmarks', 'details', folderId]);
        } catch (ex) {
            if (ex instanceof HttpErrorResponse) {
                const httpEx = <HttpErrorResponse>ex;
                if (httpEx.error.message && httpEx.error.message.indexOf('Favorite product with SKU:') > -1) {
                    this.modalService.open(
                        'Achtung',
                        // tslint:disable-next-line:max-line-length
                        'Dieses Produkt ist bereits in dieser Merkliste. Bitte wählen Sie ein anderes Produkt oder eine andere Merkliste aus.'
                    );
                }
            }
        }
    }

    private async _addProduct(folderId: number): Promise<void> {
        await firstValueFrom(this.httpClient.post(
            this.share.sharedModel?.apiUrl + this.getEndpoint() + folderId + '/products',
            {sku: this.skuToSave},
            {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                }
            }
        ));
    }

    public async deleteProduct(folderId: number, sku: string): Promise<void> {
        await firstValueFrom(this.httpClient.delete(
            this.share.sharedModel?.apiUrl + this.getEndpoint() + folderId + '/products/' + sku,
            {
                headers: {
                    'Authentication': 'Token ' + this.share.sharedModel.appPortalSecurityToken
                }
            }
        ));
    }

    public async open(sku: string): Promise<void> {
        try {
            this.skuToSave = sku;
            this.folders = await this.getAll();
            this.modal.open();
        } catch (ex) {

        }
    }

    public close(): void {
        this.modal.close();
    }

    private getEndpoint(): string {
        return this.endpoint.replace('{userId}', this.share.sharedModel.appPortalId.toString());
    }
}
